import React, { useCallback, useState } from 'react';
import Portal from 'portal/Portal';
import FormContainer from 'components/Forms';
import fieldSelection from 'components/Forms/Fields';
import Button from 'components/Button';
import { makeStyles } from '@material-ui/core/styles';
import Charts from 'components/Widgets/Charts/Charts';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { assetGridActions, assetSelectors } from './index';
import isEmpty from 'lodash/isEmpty';
import { convertToUTCTime, displayTime } from 'utils/time';
import { getItemOrDefaultToValue } from 'utils/details';
import { getTimezone } from 'reducers/utils';
import { useTranslation } from 'react-i18next';
import { validateRequired, validationObject } from 'utils/validations';
import PropTypes from 'prop-types';

const useStyles = makeStyles(theme => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    background: theme.palette.background.default,
    height: '50rem',
    overflow: 'auto',
    padding: '0.2rem',
  },
  fields: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
}));
export const validationSchema = () => {
  return validationObject({
    dateFrom: validateRequired(),
    dateTo: validateRequired(),
  });
};
const required_label = 'required_label';
const Graphs = ({ setGraphWindow, startDate = '', endDate = '' }) => {
  const [values, setValues] = useState({
    dateFrom: startDate,
    dateTo: endDate,
  });
  const [touched, setTouched] = useState({});
  const [errors, setErrors] = useState({});
  const classes = useStyles();
  const graphInputFields = [
    {
      field: 'dateFrom',
      label: 'From',
      type: 'datetime',
      size: 'half',
      required: true,
      maxDate: values.dateTo,
    },
    {
      field: 'dateTo',
      label: 'To',
      type: 'datetime',
      size: 'half',
      required: true,
      minDate: values.dateFrom,
    },
  ];
  const { t } = useTranslation();
  // get the asset detail state using useSelector
  const assetDetail = useSelector(
    state => assetSelectors.getAssetDetailsRaw(state),
    shallowEqual,
  );
  const dispatch = useDispatch();
  const dispatchFetchData = useCallback(
    (id, filters) =>
      dispatch(assetGridActions.fetchGridRowDetails({ id, filters })),
    [dispatch],
  );

  const timeZone = useSelector(state => getTimezone(state));
  const hasDataFields = asset => {
    return asset && !isEmpty(asset.assetData);
  };
  const displayFormat = 'HH:mm DD/MMM/YYYY';
  const displayNoDateFormat = 'HH:mm';
  const transformData = asset => {
    //calculate if we can use time only
    let currentDate = null;

    if (asset && !isEmpty(asset.assetData)) {
      return asset.assetData.map(d => {
        let format = displayNoDateFormat;
        if (!currentDate || currentDate === null) {
          currentDate = asset.assetData[0].timeStamp;
          format = displayFormat;
        }
        const time = displayTime({
          timestamp: getItemOrDefaultToValue(d, 'timeStamp', '-'),
          format: format,
          timeZone,
        });
        return {
          name: time,
          value: d.value,
        };
      });
    }
    return [];
  };
  const getTitle = asset => {
    if (asset && !isEmpty(asset.assetData)) {
      return `${t(asset.assetData[0].key)} (${t(asset.assetData[0].units)})`;
    } else {
      return t('Unknown data');
    }
  };
  const buttonClick = () => {
    let tempErrors = {};
    let tempTouched = {};
    //validate the form
    if (!values.dateFrom) {
      tempErrors = { ...tempErrors, dateFrom: required_label };
      tempTouched = { ...tempTouched, dateFrom: true };
    }
    if (!values.dateTo) {
      tempErrors = { ...tempErrors, dateTo: required_label };
      tempTouched = { ...tempTouched, dateTo: true };
    }
    if (values.dateFrom && values.dateTo) {
      dispatchFetchData(assetDetail.id, {
        dateFrom: convertToUTCTime({ date: values.dateFrom }),
        dateTo: convertToUTCTime({ date: values.dateTo }),
      });
    }
    setTouched(tempTouched);
    setErrors(tempErrors);
  };

  const handleChange = evt => {
    const key = evt.target.name;
    const { value } = evt.target;
    setValues({
      ...values,
      ...value,
      [key]: value,
    });
  };

  const setFieldValue = (key, val) => {
    setValues({
      ...values,
      [key]: val,
    });
  };
  const setFieldTouched = key => setTouched({ ...touched, [key]: true });
  const props = {
    values,
    touched,
    errors,
    setFieldTouched,
    handleChange,
    setFieldValue,
  };

  // make a prop data in the charts and adjust the structure according to the what the lne chart needs
  return (
    <Portal id="portal" type="modal">
      <div className={classes.container}>
        <FormContainer
          title="Graphs"
          onClickClose={() => setGraphWindow(false)}
          requireHeaderSaveBtn={false}
          size="flexible"
          noPadding={true}
          validationSchema={validationSchema}
        >
          {() => {
            return (
              <div className={classes.fields}>
                {graphInputFields.map((item, key) =>
                  fieldSelection({ item, key, props }),
                )}
              </div>
            );
          }}
        </FormContainer>
        <Button
          type="button"
          text={'Submit'}
          onClick={buttonClick}
          size="l"
          color="secondary"
        />
        {hasDataFields(assetDetail) && (
          <Charts
            title={getTitle(assetDetail)}
            data={transformData(assetDetail)}
            aspect={100}
          />
        )}
        {!hasDataFields(assetDetail) && <div>No data available</div>}
      </div>
    </Portal>
  );
};
Graphs.propTypes = {
  startDate: PropTypes.string,
  endDate: PropTypes.string,
  setGraphWindow: PropTypes.func,
};
export default Graphs;
